import React, { useReducer, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FormDataContext } from "./FormDataContext";
import "../Style/Page2.css";

const TEN_MINUTES_MS = 10 * 60 * 1000;

const FIELD_LABELS = {
    firstName: "Անուն",
    lastName: "Ազգանուն",
    patronymic: "Հայրանուն",
    passportSeries: "Անձնագրի սերիա / համար",
    htsh: "ՀԾՀ - հանրային ծառայությունների համարանիշը",
    issuedBy: "Ու՞մ կողմից է տրված",
    residence: "Անձնագրում նշված գրանցման հասցե",
    dataIssued: "Ե՞րբ է տրվել անձնագիրը",
    phone: "Հեռախոսահամար",
    email: "Էլ․ փոստ",
    confirmEmail: "Հաստատել էլ․ փոստը",
};

const REQUIRED_FIELD_ERRORS = {
    firstName: "Խնդրում ենք լրացնել ձեր անունը",
    lastName: "Խնդրում ենք լրացնել ձեր ազգանունը",
    patronymic: "Խնդրում ենք լրացնել ձեր հայրանունը",
    passportSeries: "Խնդրում ենք լրացնել Անձնագրի սերիա",
    htsh: "Խնդրում ենք լրացնել ձեր ՀԾՀ - հանրային ծառայությունների համարանիշը",
    issuedBy: "Խնդրում ենք լրացնել Ում կողմից է տրված՝ եռանիշ թիվ",
    residence: "Խնդրում ենք լրացնել Անձնագրում նշված գրանցման հասցե",
    dataIssued: "Խնդրում ենք լրացնել Ե՞րբ է տրվել անձնագիրը",
    phone: "Խնդրում ենք լրացնել ձեր հեռախոսահամարը",
    email: "Խնդրում ենք լրացնել Էլ․ փոստ",
    confirmEmail: "Խնդրում ենք Հաստատել էլ․ փոստը",
};

const ERROR_MESSAGES = {
    invalidPhone: "Խնդրում ենք լրացնել ձեր հեռախոսահամարը",
    invalidEmail: "Խնդրում ենք լրացնել վավեր Էլ․ փոստ",
    emailMismatch: "Էլ․ փոստի դաշտերը պետք է համընկնեն։",
    residenceMustContainNumber: "Հասցեն պետք է պարունակի գոնե մեկ թիվ։",
    mustContainLetterAndNumber: "Պետք է պարունակի առնվազն մեկ թիվ",
};

const safeParseJSON = (raw) => {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

const getSavedFormData = () => {
    const savedData = safeParseJSON(sessionStorage.getItem("page2FormData"));
    const savedTime = sessionStorage.getItem("page2FormTime");

    if (savedData && savedTime) {
        const currentTime = new Date().getTime();
        if (currentTime - parseInt(savedTime, 10) < TEN_MINUTES_MS) {
            return { ...savedData, errors: {} };
        }
    }
    return {
        firstName: "",
        lastName: "",
        patronymic: "",
        passportSeries: "",
        htsh: "",
        issuedBy: "",
        residence: "",
        dataIssued: "",
        phone: "",
        email: "",
        confirmEmail: "",
        errors: {},
    };
};

const reducer = (state, action) => {
    switch (action.type) {
        case "SET_FIELD":
            return { ...state, [action.field]: action.value };
        case "SET_ERRORS":
            return { ...state, errors: action.payload };
        case "RESET":
            return getSavedFormData();
        default:
            return state;
    }
};

const Page2 = () => {
    const navigate = useNavigate();
    const { updateFormData } = useContext(FormDataContext);
    const [state, dispatch] = useReducer(reducer, getSavedFormData());

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const nonEmptyFields = Object.values(state).some((value) => value !== "" && typeof value === "string");
        if (nonEmptyFields) {
            sessionStorage.setItem("page2FormData", JSON.stringify(state));
            sessionStorage.setItem("page2FormTime", new Date().getTime().toString());
        }
    }, [state]);

    // Clears the form 10 minutes after the *last edit*, not 10 minutes after
    // mount — this is a true inactivity timer (it used to be a flat timeout
    // from page load, which could wipe the form while someone was still
    // actively typing).
    useEffect(() => {
        const timer = setTimeout(() => {
            sessionStorage.removeItem("page2FormData");
            sessionStorage.removeItem("page2FormTime");
            dispatch({ type: "RESET" });
        }, TEN_MINUTES_MS);
        return () => clearTimeout(timer);
    }, [state]);

    const validateFields = () => {
        const newErrors = {};

        Object.entries(state).forEach(([key, value]) => {
            if (!value && key !== "errors") newErrors[key] = REQUIRED_FIELD_ERRORS[key];
        });

        if (!/^\d+$/.test(state.phone)) {
            newErrors.phone = ERROR_MESSAGES.invalidPhone;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
            newErrors.email = ERROR_MESSAGES.invalidEmail;
        }

        if (state.email !== state.confirmEmail) {
            newErrors.confirmEmail = ERROR_MESSAGES.emailMismatch;
        }

        if (!/\d/.test(state.residence)) {
            newErrors.residence = ERROR_MESSAGES.residenceMustContainNumber;
        }

        dispatch({ type: "SET_ERRORS", payload: newErrors });
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateFields()) {
            updateFormData(state);
            navigate("/page3");
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
        const newErrors = { ...state.errors };

        if (name === "phone") {
            newValue = value.replace(/\D/g, "");
        }

        if (name === "email" || name === "confirmEmail") {
            newValue = value.replace(/[^a-zA-Z0-9@._-]/g, "");
        }

        if (name === "residence") {
            if (!/\d/.test(newValue)) {
                newErrors.residence = ERROR_MESSAGES.residenceMustContainNumber;
            } else {
                delete newErrors.residence;
            }
        }

        if (name === "passportSeries") {
            const hasNumber = /[0-9]/.test(newValue);

            if (!hasNumber) {
                newErrors[name] = ERROR_MESSAGES.mustContainLetterAndNumber;
            } else {
                delete newErrors[name];
            }
        }

        dispatch({
            type: "SET_ERRORS",
            payload: newErrors,
        });

        dispatch({
            type: "SET_FIELD",
            field: name,
            value: newValue,
        });
    };

    return (
        <div className="page2">
            <div className="container">
                <h1>Ձեր անձնական տվյալների մուտքագրում</h1>
                {Object.keys(state)
                    .filter((key) => key !== "errors")
                    .map((key) => (
                        <div key={key} className="form-group">
                            <label htmlFor={key}>{FIELD_LABELS[key]}</label>
                            <input
                                type={key === "dataIssued" ? "date" : key.includes("email") ? "email" : key === "phone" ? "tel" : "text"}
                                name={key}
                                id={key}
                                value={state[key]}
                                onChange={handleInputChange}
                                className={`form-input ${state.errors[key] ? "input-error" : "input-success"}`}
                            />
                            {state.errors[key] && <p className="error-text">{state.errors[key]}</p>}
                        </div>
                    ))}
                <div className="btn_b_n">
                    <button type="button" className="submit-btn" onClick={handleBack}>
                        Նախորդ էջ
                    </button>
                    <button type="button" className="submit-btn" onClick={handleSubmit}>
                        Հաջորդ էջ
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Page2;
